<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card dark class="exercise-dialog-bg d-flex flex-column" style="height: 100%; position: relative; overflow: hidden">
      <!-- Neon Background Glow -->
      <div class="selection-dialog-bg-glow"></div>

      <v-toolbar flat dark color="transparent" class="flex-grow-0 pt-2" style="position: relative; z-index: 2">
        <v-btn icon dark @click="$emit('update:dialog', false)" style="background: rgba(255, 255, 255, 0.05); border-radius: 50%">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-black text-h5 pl-4">{{ dialogTitle }}</v-toolbar-title>
      </v-toolbar>

      <v-card-text class="pa-4 pt-0 flex-grow-1 overflow-y-auto" style="position: relative; z-index: 1">
        <div v-if="mode === 'swap' && exerciseToSwap" class="context-card mb-4">
          <div class="context-label">Tausche aus:</div>
          <v-list-item class="px-0">
            <v-list-item-avatar tile size="50" class="rounded-lg">
              <v-img :src="getImageForExercise(exerciseToSwap)"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold white--text">{{ exerciseToSwap.title }}</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip
                  v-for="tag in getMuscleGroups(exerciseToSwap)"
                  :key="tag"
                  x-small
                  dark
                  color="rgba(255, 255, 255, 0.2)"
                  class="muscle-chip mr-1">
                  {{ tag }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </div>

        <div class="filter-section mb-3 d-flex align-center">
          <v-text-field
            v-model="exerciseSearchInput"
            :label="searchLabel"
            filled
            rounded
            dense
            clearable
            solo
            flat
            hide-details
            background-color="rgba(255, 255, 255, 0.1)"
            prepend-inner-icon="mdi-magnify"
            class="mr-2"></v-text-field>
          <v-menu offset-y :close-on-content-click="false" max-height="400">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="grey darken-2" v-bind="attrs" v-on="on" class="white--text">
                <v-icon left>mdi-filter-variant</v-icon> Filter
                <v-badge v-if="selectedTags.length > 0" color="primary" :content="selectedTags.length" inline class="ml-2"></v-badge>
              </v-btn>
            </template>
            <v-card class="glass-card pa-3" dark width="350">
              <v-text-field v-model="tagSearch" label="Filter suchen..." dense outlined hide-details class="mb-3"></v-text-field>
              <v-chip-group dark v-model="selectedTags" column multiple active-class="primary--text">
                <v-chip v-for="tag in filteredTags" :value="tag" :key="tag" filter outlined>{{ tag }}</v-chip>
              </v-chip-group>
            </v-card>
          </v-menu>
        </div>

        <!-- REPLACED LIST CONTAINER WITH VIRTUAL SCROLL FOR PERFORMANCE -->
        <div class="list-container" style="height: 80vh">
          <v-row v-if="!listReady" class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-row>
          <v-virtual-scroll v-else :items="filteredExercises" :height="listContainerHeight" item-height="100" class="virtual-scroll-container">
            <template v-slot:default="{ item }">
              <div class="px-1 pb-2">
                <div @click="selectExercise(item)" class="exercise-item-glass d-flex align-center pa-3" v-ripple>
                  <!-- IMAGE FIX: Fixed size and cover -->
                  <v-img
                    :src="getImageForExercise(item)"
                    :lazy-src="getPlaceholderImage()"
                    class="rounded-lg mr-4 bg-grey darken-4"
                    width="70"
                    height="70"
                    max-width="70"
                    max-height="70"
                    aspect-ratio="1">
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-5" size="20"></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>

                  <div class="exercise-content overflow-hidden">
                    <div class="font-weight-bold white--text text-subtitle-1 text-truncate">{{ item.title }}</div>
                    <div class="grey--text text--lighten-1 text-caption d-flex align-center">
                      <v-icon x-small color="grey" class="mr-1">mdi-dumbbell</v-icon>
                      {{ item.deviceShortForm || "Allgemein" }}
                    </div>
                  </div>

                  <div class="action-icon ml-2">
                    <v-btn icon small color="rgba(255,255,255,0.1)">
                      <v-icon color="white">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </template>
          </v-virtual-scroll>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"
import api from "@/api" // Import der API-Funktionen

export default {
  name: "ExerciseSelectionDialog",
  props: {
    dialog: { type: Boolean, required: true },
    mode: { type: String, default: "swap", validator: (val) => ["swap", "add"].includes(val) },
    insertionIndex: { type: Number, default: 0 },
    exerciseToSwap: { type: Object, default: null },
  },
  data() {
    return {
      listReady: false,
      exerciseSearch: "",
      exerciseSearchInput: "",
      tagSearch: "",
      selectedTags: [],
      tags: [],
      searchDebounce: null,
    }
  },
  computed: {
    ...mapGetters("deviceStore", ["exerciseList"]),

    dialogTitle() {
      return this.mode === "swap" ? "Übung austauschen" : "Übung hinzufügen"
    },

    searchLabel() {
      return this.mode === "swap" ? "Finde passenden Ersatz..." : "Suche eine Übung..."
    },

    listContainerHeight() {
      // Height calculation for the scroll area
      // We return a CSS calc string which v-virtual-scroll accepts in recent Vuetify versions,
      // or we can bind it to the container styles.
      // Let's try passing it directly to the height prop which accepts string/number.
      const toolbarHeight = 64
      const filterSectionHeight = 60
      const contextCardHeight = this.mode === "swap" && this.exerciseToSwap ? 86 : 0
      const bottomPadding = 16
      const totalOffset = toolbarHeight + filterSectionHeight + contextCardHeight + bottomPadding
      return `calc(100vh - ${totalOffset}px)`
    },

    filteredTags() {
      if (!this.tagSearch) return this.tags
      return this.tags.filter((tag) => tag.toLowerCase().includes(this.tagSearch.toLowerCase()))
    },

    filteredExercises() {
      if (!this.exerciseList) return []

      const searchLower = this.exerciseSearch ? this.exerciseSearch.toLowerCase() : ""

      const baseList =
        this.mode === "swap" && this.exerciseToSwap ? this.exerciseList.filter((ex) => ex._id !== this.exerciseToSwap._id) : this.exerciseList

      return baseList.filter((exercise) => {
        if (exercise.deleted || exercise.deviceCategory === "(ALT) MedX Kraftgeräte") {
          return false
        }

        const matchesTags = this.selectedTags.length === 0 || (exercise.tags && exercise.tags.some((tag) => this.selectedTags.includes(tag.name)))
        if (!matchesTags) return false

        if (searchLower === "") return true

        return (
          exercise.title.toLowerCase().includes(searchLower) ||
          (exercise.deviceShortForm && exercise.deviceShortForm.toLowerCase().includes(searchLower))
        )
      })
    },
  },
  watch: {
    dialog: {
      immediate: true,
      handler(val) {
        if (val) {
          // Warte ca. 400ms (länger als die Standard-Transition),
          // bis der Dialog komplett steht.
          setTimeout(() => {
            this.listReady = true
          }, 400)
        } else {
          this.listReady = false
        }
      },
    },
    exerciseSearchInput(newValue) {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.exerciseSearch = newValue
      }, 300)
    },
  },
  async created() {
    try {
      const fetchedTags = await api.fetchExerciseTags()
      this.tags = [...new Set(fetchedTags.map((tag) => tag.name || tag).sort())]
    } catch (error) {
      console.error("Fehler beim Laden der Übungs-Tags:", error)
      this.$toast.error("Filter-Tags konnten nicht geladen werden.")
    }
  },
  methods: {
    selectExercise(exercise) {
      console.log(exercise._id)
      if (this.mode === "swap") {
        this.$emit("swap-selected", {
          newExercise: exercise,
          exerciseIndex: this.exerciseToSwap.originalIndex,
        })
      } else {
        this.$emit("add-selected", {
          newExercise: exercise,
          atIndex: this.insertionIndex,
        })
      }
      this.$emit("update:dialog", false)
    },
    getMuscleGroups(exercise) {
      if (!exercise || !exercise.tags) return []
      return exercise.tags.map((t) => t.name).slice(0, 4)
    },
    getImageForExercise(exercise) {
      if (exercise.picture) return `/img/uebungen/${exercise.picture.replace(/\.[^/.]+$/, "")}.webp`
      if (exercise.manual && exercise.manual[0]?.picture) {
        return `/img/uebungen/${exercise.manual[0].picture.replace(/\.[^/.]+$/, "")}.webp`
      }
      return this.getPlaceholderImage()
    },
    getPlaceholderImage() {
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
  },
}
</script>

<style scoped>
.exercise-dialog-bg {
  background: #121212 !important; /* Deep dark background */
}

.selection-dialog-bg-glow {
  position: absolute;
  width: 100%;
  height: 60%;
  top: 0;
  left: 0;
  background: radial-gradient(circle at 50% 0%, rgba(33, 150, 243, 0.12), transparent 75%);
  pointer-events: none;
  z-index: 0;
}

/* Premium Glass Item Style */
.exercise-item-glass {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.exercise-item-glass:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(33, 150, 243, 0.25);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.exercise-item-glass:active {
  transform: translateY(1px) scale(0.99);
  background: rgba(255, 255, 255, 0.05);
}

.list-container {
  overflow-y: hidden; /* Virtual scroll handles overflow */
}

.context-card {
  background-color: rgba(255, 255, 255, 0.04);
  border-radius: 20px;
  padding: 10px 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
.context-label {
  font-size: 0.75rem;
  color: #a0a0a0;
  margin-bottom: -4px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

/* Filter Menu Card */
.glass-card {
  background: rgba(30, 30, 30, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 16px !important;
}

.exercise-content {
  flex-grow: 1;
  min-width: 0;
}

.muscle-chip {
  backdrop-filter: blur(5px);
  font-size: 0.65rem !important;
  padding: 0 6px;
}
</style>
